import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";

interface Slide {
  id: number;
  type: "movie" | "tv";
  image: string;
  name: string;
  meta: string;
  tagline: string;
}

const slides: Slide[] = [
  {
    id: 1,
    type: "movie",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20frozen%20earth%20with%20a%20colossal%20planetary%20engine%20firing%20into%20deep%20blue%20space%2C%20icy%20terrain%20and%20glowing%20amber%20exhaust%2C%20epic%20science%20fiction%20atmosphere%2C%20moody%20high%20contrast%20lighting&width=1600&height=900&seq=hero-01&orientation=landscape",
    name: "流浪地球2",
    meta: "科幻 · 灾难 · 2023",
    tagline: "人类带着地球，踏上漫长的星际流浪",
  },
  {
    id: 11,
    type: "movie",
    image:
      "https://readdy.ai/api/search-image?query=Epic%20desert%20landscape%20at%20dusk%20with%20towering%20sand%20dunes%20and%20a%20lone%20traveler%20in%20a%20flowing%20cloak%2C%20warm%20golden%20sunlight%20against%20deep%20indigo%20sky%2C%20cinematic%20science%20fiction%20scale&width=1600&height=900&seq=hero-02&orientation=landscape",
    name: "沙丘2",
    meta: "科幻 · 冒险 · 2024",
    tagline: "命运指引他走向沙丘深处",
  },
  {
    id: 21,
    type: "tv",
    image:
      "https://readdy.ai/api/search-image?query=Moody%20night%20city%20street%20in%20the%202000s%20with%20warm%20street%20lamps%20and%20a%20silhouetted%20figure%20in%20a%20long%20coat%2C%20teal%20and%20amber%20cinematic%20tones%2C%20tense%20crime%20drama%20atmosphere&width=1600&height=900&seq=hero-03&orientation=landscape",
    name: "狂飙",
    meta: "犯罪 · 剧情 · 2023",
    tagline: "二十年较量，正义与沉沦的长路",
  },
  {
    id: 10,
    type: "movie",
    image:
      "https://readdy.ai/api/search-image?query=Dramatic%20desert%20test%20site%20at%20night%20with%20a%20huge%20orange%20mushroom%20cloud%20rising%20against%20a%20deep%20black%20sky%2C%20intense%20historical%20epic%20lighting%2C%20high%20contrast%20cinematic%20composition&width=1600&height=900&seq=hero-04&orientation=landscape",
    name: "奥本海默",
    meta: "剧情 · 传记 · 2023",
    tagline: "改变世界的那一刻，他成为死神",
  },
  {
    id: 23,
    type: "tv",
    image:
      "https://readdy.ai/api/search-image?query=Nostalgic%20autumn%20scene%20of%20an%20empty%20railway%20beside%20an%20old%20factory%20yard%2C%20faded%20warm%20sepia%20and%20muted%20green%20tones%2C%20melancholic%20cinematic%20mood%20with%20soft%20mist&width=1600&height=900&seq=hero-05&orientation=landscape",
    name: "漫长的季节",
    meta: "悬疑 · 剧情 · 2023",
    tagline: "跨越二十年的真相，在秋天浮现",
  },
  {
    id: 27,
    type: "tv",
    image:
      "https://readdy.ai/api/search-image?query=Grand%20ancient%20Chinese%20palace%20hall%20lit%20by%20hundreds%20of%20candles%2C%20rich%20crimson%20and%20gold%20tones%2C%20majestic%20historical%20drama%20atmosphere%2C%20cinematic%20depth%20and%20warm%20glow&width=1600&height=900&seq=hero-06&orientation=landscape",
    name: "庆余年第二季",
    meta: "古装 · 剧情 · 2024",
    tagline: "庙堂江湖，步步为营",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const goTo = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  const handleTouchStart = (event: TouchEvent) => {
    touchStart.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStart.current = null;
  };

  const current = slides[index];

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-primary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, slideIndex) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.name}
            title={`${slide.name} - 高清影视在线观看`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>

      <div className="relative z-10 h-full w-full flex items-end md:items-center">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 pb-16 md:pb-0">
          <div key={current.id} className="max-w-xl animate-fade-up">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-white mb-4">
              最新推荐
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
              {current.name}
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/80">{current.meta}</p>
            <p className="mt-2 text-base md:text-lg text-white/90">{current.tagline}</p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={current.type === "movie" ? "/movie" : "/tv"}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/15 text-white text-sm font-medium hover:bg-white/25 transition-colors backdrop-blur-sm whitespace-nowrap"
              >
                查看同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-black/30 text-white hover:bg-accent-500 transition-colors cursor-pointer backdrop-blur-sm"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-black/30 text-white hover:bg-accent-500 transition-colors cursor-pointer backdrop-blur-sm"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {slides.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(slideIndex)}
            aria-label={`第 ${slideIndex + 1} 张`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              slideIndex === index ? "w-7 bg-accent-500" : "w-2 bg-white/60 hover:bg-white"
            }`}
          />
        ))}
      </div>
    </section>
  );
}